import { lazy, memo, Suspense, useState } from "react";
import { FormattedMessage } from "react-intl";
import styles from "./styles.module.scss";
import DocumentsIcon from "@/common/svg/documents-icon.svg";
import { useFetchProfile } from "@/entities/profile/api/useFetchProfile";
import { ChevronButton } from "@/shared/ui/Buttons/ChevronButton";
import { PageSpinner } from "@/shared/ui/PageSpinner";
import { SkeletonList } from "@/shared/ui/SkeletonList";
import { Typography } from "shared/ui/Typography";
import { GRAY_COLOR } from "@/styles/colorsJS";

interface Props {
  showDocuments?: boolean;
}

const ClientDocuments = lazy(
  () =>
    import(
      /* webpackChunkName: "client-documents", webpackPrefetch: true */ "@/features/ClientDocuments"
    ),
);

export const ProfileCard = memo(({ showDocuments = false }: Props) => {
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);

  const toggleDocumentsModal = () => {
    setShowDocumentsModal(!showDocumentsModal);
  };

  const { profile, loadingProfile } = useFetchProfile();

  return (
    <>
      <div className={styles.wrapper}>
        {loadingProfile && <SkeletonList height="h-15" />}
        {!loadingProfile && profile && (
          <>
            <div className={styles.profile}>
              <div className={styles.avatarWrapper}>
                {!!profile?.avatar?.url && (
                  <img
                    className={styles.avatar}
                    src={profile.avatar.url}
                    alt="profile image"
                  />
                )}
              </div>
              <div>
                <Typography as="h2" type="title">
                  {profile.name}
                </Typography>
                <Typography as="h3" color={GRAY_COLOR}>
                  {profile.email}
                </Typography>
              </div>
            </div>
            {!!profile?.hasCreditLine && showDocuments && (
              <div className={styles.documents}>
                <ChevronButton
                  onClick={toggleDocumentsModal}
                  text={<FormattedMessage defaultMessage="Документы клиента" />}
                  Icon={<DocumentsIcon />}
                />
              </div>
            )}
          </>
        )}
      </div>
      {showDocumentsModal && (
        <Suspense fallback={<PageSpinner />}>
          <ClientDocuments
            show={showDocumentsModal}
            onClose={toggleDocumentsModal}
          />
        </Suspense>
      )}
    </>
  );
});

ProfileCard.displayName = "ProfileName";
